import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteMST1110_R01, disableMST1110_R01, downloadExcel_MST1110_R01, getMST1110_R01AllCount, getMST1110_R01List, getPdfBlob_MST1110_R01, patchMST1110_R01, postMST1110_R01 } from "./Api_MST1110_R01";
import { defaultMST1110_R01Filter, MST1110_R01Filter, useMST1110_R01Store } from "./Store_MST1110_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultMST1110_R01Dto, MST1110_R01Dto } from "./Dto_MST1110_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	MST1110_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<MST1110_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const MST1110_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		MST1110_R01Filter, setMST1110_R01Filter,
		MST1110_R01PastFilter, setMST1110_R01PastFilter,
		MST1110_R01List, setMST1110_R01List, insertMST1110_R01List, removeMST1110_R01List,
		deletedMST1110_R01List, addDeletedMST1110_R01List, removeDeletedMST1110_R01List,
		selectedMST1110_R01List, setSelectedMST1110_R01List, removeSelectedMST1110_R01List,
		originalMST1110_R01List, setOriginalMST1110_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useMST1110_R01Store();

	const { fetchOrgCdCode, wmsCdList, warehouseCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { printPdfFromBlob } = useQzPrint();

	const { company, userInfo } = useUserStore();

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("MST1110_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

    const [clickable, setClickable] = useState(true);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedMST1110_R01List.length === 0 && selectedMST1110_R01List.length === 0,
			save: MST1110_R01List.length === 0,
			print: MST1110_R01List.length === 0,
			excel: MST1110_R01List.length === 0
		}));
	}, [selectedMST1110_R01List, deletedMST1110_R01List, MST1110_R01List]);
	
	
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_MST1110_R01(
			'',
			getParams(MST1110_R01Filter),
			MST1110_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '생산계획 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [MST1110_R01Filter, MST1110_R01List])

	

	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_MST1110_R01(
				'',
				getParams(MST1110_R01Filter),
				MST1110_R01List.length ?? 0,
				company?.compCd || ''
			);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [MST1110_R01Filter, MST1110_R01List, company?.compCd, printPdfFromBlob, setLoadingSpinner, setMessage]);
	
	//신규 행 추가시 기본적으로 세팅할 데이터를 정하고 생성
	const defaultMST1110_R01Row = useCallback(() => {
		const logicCenter = wmsCdList[0]?.minorCd ?? '';
		const warehouseCdListToUse = warehouseCdList.filter(value => value.logicCenter === logicCenter);

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST1110_R01_ROW_VALIDATE, {
				wmsCdList,
				warehouseCdList: warehouseCdListToUse,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultMST1110_R01Dto };
		}

		return {
			...defaultMST1110_R01Dto,
			logicCenter,
			warehouseCd: warehouseCdListToUse[0].warehouseCd,
			inserted: true,
		};
	}, [defaultMST1110_R01Dto, wmsCdList, warehouseCdList, validateCommonCodeLists]);

	const getParams = (filter: MST1110_R01Filter) => {
		const params: Partial<MST1110_R01Filter> = {
			logicCenter: filter.logicCenter || undefined,
			warehouseCd: filter.warehouseCd || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST1110_R01AllCount(getParams(MST1110_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST1110_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}
	
		let initialListLength = MST1110_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setMST1110_R01List([]);
		}

		const responseData = await getMST1110_R01List(
			'',
			getParams(MST1110_R01Filter),
			fetchType === 'refetch' ? initialListLength : MST1110_R01List.length ?? 0
		);
	
		let newList = [...responseData.data.data ] as MST1110_R01Dto[];
		setMST1110_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalMST1110_R01List(newList);
		setSelectedMST1110_R01List([]);
		removeDeletedMST1110_R01List(deletedMST1110_R01List);
	
		if (newList.length === 0) {
			insertMST1110_R01List([{ ...defaultMST1110_R01Row(), inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setMST1110_R01PastFilter(MST1110_R01Filter);
		setLoadingSpinner(false);
	}, [
		MST1110_R01Filter,  MST1110_R01PastFilter, MST1110_R01List, deletedMST1110_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalMST1110_R01List, setSelectedMST1110_R01List, insertMST1110_R01List,
		defaultMST1110_R01Row, setScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		// rowIndex가 없으면 등록 버튼으로 추가된 행이므로 rowIndex 변수는 undefined 상태로 시작함
		// rowIndex가 있으면 그리드에서 추가된 행이므로 클릭 가능 상태 처리, rowIndex는 그리드에서 추가된 행의 인덱스 값
		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: MST1110_R01List,
			rowData: isGridInsert ? MST1110_R01List[rowIndex]	: { ...defaultMST1110_R01Row(), valueIndex: -1 },
			insertRow: insertMST1110_R01List,
			defaultRowData: defaultMST1110_R01Row(),
			...(isGridInsert && { setClickable }),
		});
	}, [MST1110_R01List, insertMST1110_R01List, defaultMST1110_R01Row, setClickable]);

	const validateInsertData = (data: MST1110_R01Dto, currentRowIndex: number) => {

		if(!validateString(data.locationCd)) {
			showWarning('로케이션코드를 입력해주세요.'); return false;
		}
		if(!validateString(data.locationNm)) {
			showWarning('로케이션명을 입력해주세요.'); return false;
		}
		if(!validateString(data.logicCenter)) {
			showWarning('물류센터를 선택해주세요.'); return false;
		}
		if(!validateString(data.warehouseCd)) {
			showWarning('창고코드를 선택해주세요.'); return false;
		}

		return true;
	}
	
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리
		const insertedList: MST1110_R01Dto[] = MST1110_R01List.filter(row => 
			!deletedMST1110_R01List.includes(row.locationCd) && 
			row.inserted === true
		);

		// 수정된 사용자만 필터링
		const modifiedList = MST1110_R01List.filter(row => 
			!deletedMST1110_R01List.includes(row.locationCd) && 
			row.isModified && !row.inserted
		);

		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = MST1110_R01List.findIndex(user => user.locationCd === value.locationCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.locationCd);
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = MST1110_R01List.findIndex(user => user.locationCd === value.locationCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.locationCd);
		});
		if(!valid) { return; }

		const deleteStringList = deletedMST1110_R01List.map(item => item.toString());
		const deleteTargetList = MST1110_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postMST1110_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchMST1110_R01(modifiedList) : Promise.resolve(),
			deletedMST1110_R01List.length > 0 ? Promise.all(deleteTargetList.map(value => deleteMST1110_R01(value.locationCd))) : Promise.resolve()
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('로케이션정보 저장이 완료되었습니다.');
			setMessage('로케이션정보 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('로케이션정보 삭제가 완료되었습니다.');
			setMessage('로케이션정보 삭제가 완료되었습니다.');
		}


		setTimeout(() => { setMessage(''); }, 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });

	}, [MST1110_R01List, deletedMST1110_R01List, originalMST1110_R01List, executeDataFetch]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedMST1110_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedMST1110_R01List(deletedMST1110_R01List);
			removeSelectedMST1110_R01List();
			setMST1110_R01List([...MST1110_R01List]);
		} else if (selectedMST1110_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedMST1110_R01List(selectedMST1110_R01List);
			removeSelectedMST1110_R01List(selectedMST1110_R01List);
		}
	}, [selectedMST1110_R01List, deletedMST1110_R01List, addDeletedMST1110_R01List, removeDeletedMST1110_R01List, removeSelectedMST1110_R01List, setMST1110_R01List, MST1110_R01List]);

	return{
		clickable, setClickable,
		handleExcelClick,
		handlePrintClick,
		defaultMST1110_R01Row,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		buttonDisabled,
	}
}
